import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

const POLL_INTERVAL_MS = 60 * 1000;
const VALID_STATUSES = new Set(['unknown', 'unused', 'ok', 'failed']);
const INITIAL_SNAPSHOT = {
  status: 'unknown',
  lastCheckedAt: null,
  lastOkAt: null,
  consecutiveFailures: 0,
  lastError: null,
};

function normalizeSnapshot(value) {
  if (!value || !VALID_STATUSES.has(value.status)) return null;

  return {
    status: value.status,
    lastCheckedAt: value.lastCheckedAt || null,
    lastOkAt: value.lastOkAt || null,
    consecutiveFailures: Number.isFinite(Number(value.consecutiveFailures))
      ? Number(value.consecutiveFailures)
      : 0,
    lastError: value.lastError ? String(value.lastError) : null,
  };
}

// Reads the server's in-memory subscription health snapshot. Endpoint or auth
// failures are intentionally silent so this diagnostic can never break the
// admin panel. A successful snapshot remains visible until a newer one arrives.
export function useSubscriptionHealth() {
  const [snapshot, setSnapshot] = useState(INITIAL_SNAPSHOT);

  useEffect(() => {
    let active = true;

    const poll = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const accessToken = session?.access_token;
        if (!accessToken) return;

        const res = await fetch('/api/admin/subscription-health', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) return;

        const next = normalizeSnapshot(await res.json());
        if (active && next) setSnapshot(next);
      } catch {
        // Fail safe: health reporting must not make the admin shell error.
      }
    };

    poll();
    const intervalId = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      active = false;
      clearInterval(intervalId);
    };
  }, []);

  return snapshot;
}
